import React, { useCallback, useEffect, useRef, useState } from "react"
import { dispositivoSemHover } from "app/utils/dispositivoSemHover"

/**
 * 493427 — segunda barra de rolagem horizontal, no TOPO de um container rolável.
 *
 * O problema que ela resolve: a barra horizontal nativa fica na BASE do container. Numa grade
 * alta, cuja base cai abaixo da borda inferior do navegador, a barra existe mas está fora da
 * tela — o usuário não tem pista de que dá para rolar para o lado (são muitas colunas) e, para
 * alcançar a barra, precisa rolar a página inteira até o fim da grade. Esta é uma barra ligada à
 * MESMA rolagem: mexer numa mexe na outra.
 *
 * Genérico de propósito — não sabe nada de orçamento nem de tabela. Basta a referência do
 * container que rola e quantas linhas ele tem:
 *
 *     <BarraRolagemHorizontalSuperior
 *         referenciaContainerRolavel={refDoContainer}
 *         quantidadeDeLinhas={itens.length}
 *         id="minha-grade_BarraRolagemSuperior"
 *     />
 *
 * Renderize-a IMEDIATAMENTE ANTES do container no JSX — quem define "em cima da grade" é a ordem
 * do DOM.
 *
 * Ela é `position: sticky`, então acompanha a rolagem da página enquanto a grade está na tela. Sem
 * isso havia um buraco: no MEIO da grade nenhuma das duas barras aparecia — a nativa (na base) ainda
 * abaixo da janela e esta já acima dela. Use `obterDeslocamentoSuperior` para ela parar ABAIXO do
 * cabeçalho fixo do app, senão para debaixo dele.
 *
 * Aparece só quando as QUATRO condições valem ao mesmo tempo (senão não renderiza nada):
 *   1. não é tablet nem celular — ver `dispositivoSemHover`;
 *   2. mais de `minimoDeLinhas` linhas — em grade curta a base está logo ali, e uma barra extra
 *      seria só ruído;
 *   3. existe rolagem horizontal de fato (`scrollWidth > clientWidth`) — sem isso a barra viria
 *      vazia, sem nada para arrastar;
 *   4. a base do container está abaixo da janela — se a barra nativa está visível, esta seria uma
 *      duplicata inútil.
 */

// "mais de 5 linhas", conforme pedido; `minimoDeLinhas` permite outro corte por grade.
const _MINIMO_DE_LINHAS_PADRAO = 5

// Zoom e telas com DPI fracionário fazem `scrollWidth` passar de `clientWidth` por menos de 1px
// sem haver rolagem real; abaixo desta folga não se considera que há overflow.
const _TOLERANCIA_LARGURA = 2

// Usada só se a medição não der um valor plausível. É a altura de `::-webkit-scrollbar` em
// Content/estilo.css — mexer lá pede conferir aqui.
const _ALTURA_BARRA_PADRAO = 10

// Acima disto o valor medido não é barra de rolagem (borda grossa, padding, layout no meio de uma
// transição): melhor cair no padrão do que desenhar uma faixa enorme.
const _ALTURA_BARRA_MAXIMA = 30

// Acima do painel de ações flutuante da grade (z-index 50 em grid-itens.module.scss, que compete
// no contexto da raiz porque `.containerGrid` é `position: relative` com z-index auto) e bem abaixo
// do cabeçalho do app (1030 em layout.css), que tem de continuar cobrindo tudo.
const _Z_INDEX = 60

type Medida = {
    larguraConteudo: number
    alturaBarra: number
    deslocamentoSuperior: number
}

export type BarraRolagemHorizontalSuperiorProps = {
    /** Container que tem a rolagem (o do `overflow-x`), não o conteúdo de dentro dele. */
    referenciaContainerRolavel: React.RefObject<HTMLElement>
    quantidadeDeLinhas: number
    /** Só exibe com MAIS linhas que isto. Padrão: 5. */
    minimoDeLinhas?: number
    /**
     * Onde a barra para ao grudar no topo, em px do alto da janela — o que estiver fixo por cima
     * (cabeçalho do app). Passe uma função de MÓDULO, não uma criada no render: a identidade dela
     * entra nas dependências do recálculo. Padrão: 0 (nada cobrindo o topo).
     */
    obterDeslocamentoSuperior?: () => number
    id?: string
}

/**
 * Altura da barra de rolagem horizontal do próprio container, para a barra de cima ter exatamente
 * a mesma espessura. Medido em vez de fixo porque cada navegador/tema desenha com uma espessura
 * diferente (o `::-webkit-scrollbar` do projeto vale só nos WebKit/Blink; Firefox usa a do
 * sistema). `offsetHeight - clientHeight` é a barra MAIS as bordas horizontais, daí descontar as
 * bordas — um container com borda grossa levaria uma faixa grossa junto.
 */
const _semDeslocamento = () => 0

const _medirAlturaBarraDeRolagem = (container: HTMLElement): number => {
    const estilo = window.getComputedStyle(container)
    const bordas = (parseFloat(estilo.borderTopWidth) || 0) + (parseFloat(estilo.borderBottomWidth) || 0)
    const medida = container.offsetHeight - container.clientHeight - bordas

    return medida > 0 && medida <= _ALTURA_BARRA_MAXIMA ? medida : _ALTURA_BARRA_PADRAO
}

export const BarraRolagemHorizontalSuperior = (props: BarraRolagemHorizontalSuperiorProps) => {
    const referenciaBarra = useRef<HTMLDivElement>(null)
    const referenciaAlturaBarra = useRef<number>(null)
    const [medida, setMedida] = useState<Medida>(null)

    const minimoDeLinhas = props.minimoDeLinhas ?? _MINIMO_DE_LINHAS_PADRAO
    const obterDeslocamentoSuperior = props.obterDeslocamentoSuperior ?? _semDeslocamento

    const recalcular = useCallback(() => {
        const container = props.referenciaContainerRolavel.current
        if (container == null) return

        // Em tablet/celular a barra nativa é overlay: aparece sozinha ao rolar e não ocupa espaço,
        // então a barra extra só roubaria altura de uma tela que já é pequena — mesmo motivo pelo
        // qual o `overflow: scroll` do CSS também vale só onde há ponteiro de verdade. Primeira
        // condição da lista de propósito: em touch nem chega a ler layout.
        if (dispositivoSemHover()) {
            setMedida(null)
            return
        }

        // Não oscila ao entrar/sair: exibir a barra empurra o container para BAIXO (a base afasta-se
        // mais da janela, condição 3 segue verdadeira) e escondê-la o traz para CIMA (a base
        // aproxima-se, condição 3 segue falsa). Os dois sentidos são estáveis, então não existe o
        // pisca-pisca clássico de "some porque apareceu, aparece porque sumiu".
        const temLinhasSuficientes = props.quantidadeDeLinhas > minimoDeLinhas
        const temRolagemHorizontal = container.scrollWidth > container.clientWidth + _TOLERANCIA_LARGURA
        const baseForaDaJanela = container.getBoundingClientRect().bottom > window.innerHeight

        if (!temLinhasSuficientes || !temRolagemHorizontal || !baseForaDaJanela) {
            setMedida(null)
            return
        }

        // A espessura da barra não muda em runtime: mede uma vez e reaproveita, para o
        // `getComputedStyle` não entrar no caminho que roda a cada quadro de rolagem.
        if (referenciaAlturaBarra.current == null)
            referenciaAlturaBarra.current = _medirAlturaBarraDeRolagem(container)

        // A barra VERTICAL do container consome largura útil DELE; a barra de cima não tem barra
        // vertical, então o `clientWidth` dela é maior e, com a mesma largura de conteúdo, o curso
        // dela sairia MENOR — arrastar a barra até o fim deixaria a grade alguns pixels antes do
        // fim. Medido no navegador: 8px de diferença (`::-webkit-scrollbar { width: 8px }`), curso
        // 1448 contra 1456. Somar `offsetWidth - clientWidth` iguala os dois cursos.
        //
        // Aqui as bordas laterais ENTRAM na conta (ao contrário de `_medirAlturaBarraDeRolagem`,
        // que as desconta): a barra de cima não tem borda, então a largura que ela precisa recuperar
        // é a barra vertical MAIS as bordas que o container tem e ela não.
        const compensacaoLarguraPerdida = container.offsetWidth - container.clientWidth

        const nova: Medida = {
            larguraConteudo: container.scrollWidth + compensacaoLarguraPerdida,
            alturaBarra: referenciaAlturaBarra.current,
            // Recalculado junto, e não uma vez só: a altura do cabeçalho fixo muda quando a janela
            // muda de largura (o conteúdo dele quebra em mais linhas).
            deslocamentoSuperior: obterDeslocamentoSuperior()
        }

        // Devolver o MESMO objeto quando nada mudou é o que impede o loop: este cálculo roda a
        // cada render (é assim que ele percebe linha/coluna nova), e um objeto novo a cada vez
        // faria o `setMedida` render de novo para sempre. Com a referência igual, o React aborta.
        setMedida(anterior =>
            anterior != null
                && anterior.larguraConteudo === nova.larguraConteudo
                && anterior.alturaBarra === nova.alturaBarra
                && anterior.deslocamentoSuperior === nova.deslocamentoSuperior
                ? anterior
                : nova)
    }, [props.referenciaContainerRolavel, props.quantidadeDeLinhas, minimoDeLinhas, obterDeslocamentoSuperior])

    // Rolagem e redimensionamento da JANELA mudam a condição 3 (base abaixo da janela).
    useEffect(() => {
        let agendado = false

        // Coalescer por quadro, não é debounce: o navegador dispara vários `scroll` entre dois
        // quadros e aqui se faz leitura de layout (getBoundingClientRect/scrollWidth). Uma leitura
        // por quadro é o teto útil — mais que isso é trabalho jogado fora. Vale porque o gatilho é
        // rolagem, que não suja o layout; para observador de MUTAÇÃO de DOM o rAF seria a
        // ferramenta errada, já que a mutação suja o layout e cada leitura obriga um recálculo.
        const agendarRecalculo = () => {
            if (agendado) return
            agendado = true
            requestAnimationFrame(() => {
                agendado = false
                recalcular()
            })
        }

        window.addEventListener("scroll", agendarRecalculo, { passive: true })
        window.addEventListener("resize", agendarRecalculo)

        return () => {
            window.removeEventListener("scroll", agendarRecalculo)
            window.removeEventListener("resize", agendarRecalculo)
        }
    }, [recalcular])

    // Sem array de dependências: roda a cada render, que é justamente quando o conteúdo pode ter
    // mudado (linha incluída/excluída, coluna ligada/desligada, fonte da grade alterada) — sem
    // precisar de MutationObserver para descobrir isso.
    useEffect(() => { recalcular() })

    // As duas barras espelham uma à outra. Não há loop: atribuir a `scrollLeft` o valor que ele já
    // tem não dispara evento de rolagem no navegador, então o eco morre na primeira volta.
    useEffect(() => {
        const container = props.referenciaContainerRolavel.current
        const barra = referenciaBarra.current
        if (medida == null || container == null || barra == null) return

        barra.scrollLeft = container.scrollLeft

        const espelharDoContainerNaBarra = () => { barra.scrollLeft = container.scrollLeft }

        container.addEventListener("scroll", espelharDoContainerNaBarra, { passive: true })

        return () => container.removeEventListener("scroll", espelharDoContainerNaBarra)
    }, [medida, props.referenciaContainerRolavel])

    if (medida == null) return null

    return <div
        ref={referenciaBarra}
        id={props.id}
        style={{
            // `scroll` e não `auto`: é para a barra estar sempre desenhada — ela É o componente.
            overflowX: "scroll",
            // Sem isto, um filho de 1px de altura dentro de uma caixa que a barra ocupa inteira
            // pode gerar barra vertical também.
            overflowY: "hidden",
            height: medida.alturaBarra,
            // Acompanha a rolagem da página enquanto a grade está na tela, parando abaixo do que
            // estiver fixo no topo. O `sticky` é limitado pelo elemento PAI, o que aqui ajuda: ao
            // passar da grade a barra é liberada — e nesse ponto a condição 4 já a escondeu.
            position: "sticky",
            top: medida.deslocamentoSuperior,
            zIndex: _Z_INDEX,
            // Fundo opaco: grudada, a barra fica POR CIMA das linhas da grade, e sem isso o conteúdo
            // apareceria através da faixa (o trilho de `::-webkit-scrollbar-track-piece` só cobre
            // onde o navegador desenha a barra).
            backgroundColor: "var(--mu-superficie, #fff)"
        }}
        onScroll={() => {
            const container = props.referenciaContainerRolavel.current
            if (container != null) container.scrollLeft = referenciaBarra.current.scrollLeft
        }}
    >
        {/* Só existe para dar largura de conteúdo a esta barra — é o que faz o polegar dela ter o
            mesmo tamanho e o mesmo curso do polegar da barra nativa da grade (ver
            `compensacaoLarguraPerdida`, que ajusta o valor para os cursos baterem exatamente). */}
        <div style={{ width: medida.larguraConteudo, height: 1 }} />
    </div>
}

export default BarraRolagemHorizontalSuperior
